/* eslint-disable no-plusplus */
import React, { FC, useContext, useRef, useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParam,
} from 'use-query-params';
import { useDebouncedCallback } from 'use-debounce';
import { DatePickerContext } from '@/features/details/date-pick/context';
import {
  ExpVersion,
  SummaryResponse,
  useAnalyticsApi,
} from '@/webapi/use-analytics-api';
import {
  maybe,
  notProfitMetric,
  notReturnsMetric,
} from '@/features/details/utils';
import {
  getAllowedGoals,
  isProfitMetricAvailable,
  isReturnsMetricAvailable,
  MetricKind,
  MetricValue,
} from '@/webapi/models';
import {
  ElementTracking,
  Experience,
  ExperienceTag,
  ExperienceVariant,
} from '@/webapi/use-experience-api';
import {
  CONTROL,
  DELETED,
  getMetricsStartDate,
} from '@/features/details/shared';
import { shouldDisplaySummaryMetrics } from '@/utils/data_freshness';
import { formatCustomGoalName } from '@/features/shared/labels-strip';
import { AccountContext, useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { isPriceTestGoal } from '@/features/details/price-test-reports/shared';
import {
  getStoreCustomEnrich,
  getStoreCustomEnrichKeys,
  mergeStoreMetrics,
} from '@/features/details/summary/store-enrich-metrics';
import { isObjEmpty } from '@/features/details/summary/util';

interface SummaryContext {
  showMoreMetrics: boolean;
  showSummary: boolean;
  hasOneVariant: boolean;
  primaryGoal: MetricKind;
  data: SummaryResponse;
  variants: Array<ExperienceVariant>;
  moreMetrics: Array<MetricKind>;
  primaryMetrics: Array<MetricKind>;
  largeHeaders: boolean;
  hasMoreMetrics: boolean;
  metricsPerRow: number;
  metricsStartAt?: Date;
  hasMetric: (metric: MetricKind) => boolean;
  getCustomMetricName: (metric: MetricKind) => string;
  showPriceTestReport: boolean;
  setShowPriceTestReport: (_: boolean) => void;
  isPriceTestReportEnabled: boolean;
  expId: string;
  elementTracking?: ElementTracking[];
}

export const SummaryContext = React.createContext({} as SummaryContext);

export const SummaryProvider: FC<{
  experience: Experience;
  children?: React.ReactNode;
}> = ({ experience, children }) => {
  const summaryContext = newSummaryContext(experience);

  return (
    <SummaryContext.Provider value={summaryContext}>
      {children}
    </SummaryContext.Provider>
  );
};

function isVariantOn100(experience: Experience, version: ExpVersion) {
  const variantWith100Chance = experience?.variants?.find(
    (v) => v.publishedChance === 100,
  );
  return (
    !!variantWith100Chance &&
    variantWith100Chance?.name !== CONTROL &&
    version?.isCurrent
  );
}

function getVariants(
  data: SummaryResponse,
  experience: Experience,
  variantOn100: boolean,
) {
  const ids = data?.variants?.map((x) => x.variantId);
  let variants =
    experience?.variants?.filter((v) => !!ids?.includes(v.id)) || [];
  variants = [
    ...variants,
    ...maybe(
      () =>
        data.variants
          .filter((v) => !variants.map((v) => v.id).includes(v.variantId))
          .map(
            (v) => ({ id: v.variantId, name: DELETED } as ExperienceVariant),
          ),
      [],
    ),
  ];
  return variantOn100 ? variants?.filter((v) => v.name !== CONTROL) : variants;
}

const minStartDateOfSmoothRevenue = new Date(`2025-05-13T00:00:00`).valueOf();
export const isSmoothRevenueAvailable = (metricsStartAt: string) =>
  new Date(metricsStartAt).valueOf() > minStartDateOfSmoothRevenue &&
  getMetricsStartDate().valueOf() > minStartDateOfSmoothRevenue;

export const isOtwForAllEnabled = () => {
  const [outlierSmooth] = useQueryParam<boolean>(`otw4all`, BooleanParam);
  return outlierSmooth;
};

export const isSmoothOutlierEnabled = (experience: Experience) => {
  const isOutliersEnabled = useFeatureBit(FeatureBit.METRICS_OUTLIERS);
  const [outlierSmooth] = useQueryParam<boolean>(`outlier`, BooleanParam);

  const { metricsStartAt } = experience;
  return (
    outlierSmooth &&
    isOutliersEnabled &&
    !experience.isPostPurchase &&
    isSmoothRevenueAvailable(metricsStartAt)
  );
};

export const findActiveVariants = (
  experience: Experience,
  version: ExpVersion,
) =>
  Object.keys(
    experience?.versions?.find((v) => v?.version === version?.version)
      ?.variantChances || {},
  );

const getDefaultChartsPrimaryGoal = (
  experience: Experience,
  goals: MetricKind[],
) => {
  let primaryGoal = experience?.primaryGoal || goals?.[0];
  if (isPriceTestGoal(primaryGoal)) {
    primaryGoal = MetricKind.CONVERSION_RATE;
  }
  return primaryGoal;
};

export function newSummaryContext(experience: Experience) {
  const { version, startDate, endDate } = useContext(DatePickerContext);
  const accountCtx = useContext(AccountContext);
  const alias = accountCtx?.account?.store?.alias || ``;
  const { getSummary, loading, getCustomMetrics } = useAnalyticsApi();
  const [data, setData] = useState<SummaryResponse>(undefined);
  const [v] = useQueryParam(`v`, NumberParam);
  const [sessionsType] = useQueryParam(`st`, StringParam);
  const [audience] = useQueryParam(`aud`, StringParam);
  const customMetrics = useRef(new Map<number, string>());
  const isPriceTestReportsAvailable = isPriceTestReportsEnabled(experience);
  const [showPriceTestReport, setShowPriceTestReport] = useState(
    experience.openPriceTestReport,
  );

  const activeVariants = findActiveVariants(experience, version);

  useAsyncEffect(async () => {
    getCustomMetrics().then((resp) => {
      resp?.metrics?.forEach((metric) => {
        customMetrics.current.set(metric.id, metric.name);
      });
    });
  }, []);

  const debouncedDataFEtch = useDebouncedCallback(summaryDataFetch, 300);
  const isOutlierSmooth = isSmoothOutlierEnabled(experience);
  const isOtwForAll = isOtwForAllEnabled();

  useAsyncEffect(async () => {
    await debouncedDataFEtch(
      startDate,
      endDate,
      getSummary,
      experience,
      alias,
      v,
      version,
      setData,
      sessionsType,
      audience,
      activeVariants,
      isOutlierSmooth,
      isOtwForAll,
    );
  }, [
    startDate?.toLocaleDateString(),
    endDate?.toLocaleDateString(),
    v,
    sessionsType,
    audience,
    isOutlierSmooth,
    isOtwForAll,
  ]);
  const variantOn100 = isVariantOn100(experience, version);
  const variants = getVariants(data, experience, variantOn100);
  const storeMetrics = getStoreCustomEnrichKeys(alias);
  const goals = getAllowedGoals(experience?.goals, experience.tag);
  const primaryGoal = getDefaultChartsPrimaryGoal(experience, goals);

  const hasOneVariant = variants?.length === 1 || variantOn100;
  const showSummary = shouldDisplaySummaryMetrics(data, experience, version);

  const showMoreMetrics = goals?.length > 0 && showSummary;

  const isContent = experience.tag === ExperienceTag.CONTENT;
  const primaryMetrics = new Set([
    MetricKind.SESSIONS,
    MetricKind.TRANSACTIONS,
    MetricKind.CONVERSION_RATE,
    MetricKind.AVG_ORDER_VALUE,
    MetricKind.PER_SESSION_VALUE,
    MetricKind.REVENUE,
  ]);

  const contentMetrics = [
    MetricKind.CLICKS,
    MetricKind.CTR,
    MetricKind.REVENUE_FROM_CLICKS,
  ];

  const freeSlotsInFirstRow = calcFreeSlotsInFirstRow({
    hasOneVariant,
  });
  let moreMetrics = goals?.filter((m) => !primaryMetrics.has(m));
  if (isContent) {
    const set = new Set(moreMetrics);
    contentMetrics.forEach((m) => set.add(m));
    moreMetrics = Array.from(set);
  }
  if (!isProfitMetricAvailable(experience.metricsStartAt)) {
    moreMetrics = moreMetrics?.filter(notProfitMetric());
  }
  if (!isReturnsMetricAvailable(experience.metricsStartAt)) {
    moreMetrics = moreMetrics?.filter(notReturnsMetric());
  }
  for (let i = 0; i < freeSlotsInFirstRow && !!moreMetrics?.[i]; i++) {
    primaryMetrics.add(moreMetrics?.[i]);
  }
  moreMetrics = moreMetrics?.filter((m) => !primaryMetrics.has(m));
  moreMetrics = mergeStoreMetrics(moreMetrics, storeMetrics);

  const hasMoreMetrics = showMoreMetrics && moreMetrics.length > 0;
  const metricsPerRow = primaryMetrics.size + (!hasOneVariant ? 2 : 0);
  const largeHeaders = metricsPerRow <= 6;

  const metricsStartAt: Date | undefined =
    experience.metricsStartAt && new Date(experience.metricsStartAt);
  const hasMetric = (metric: MetricKind) =>
    data?.variants
      ?.map((v) => v?.metrics?.find((m) => m.name === metric)?.value > 0)
      .find((x) => x);
  const getCustomMetricName = (m: MetricKind) =>
    formatCustomGoalName(m, customMetrics.current);
  return {
    metricsStartAt,
    showMoreMetrics,
    showSummary,
    primaryGoal,
    data,
    hasOneVariant,
    variants,
    largeHeaders,
    hasMoreMetrics,
    moreMetrics,
    metricsPerRow,
    primaryMetrics: Array.from(primaryMetrics),
    loading,
    hasMetric,
    getCustomMetricName,
    showPriceTestReport,
    setShowPriceTestReport,
    version,
    activeVariants,
    isPriceTestReportEnabled: isPriceTestReportsAvailable,
    expId: experience.id,
    elementTracking: experience?.elementTracking || [],
  };
}

export function isPriceTestReportsEnabled(experience: Experience) {
  const isAdminAvailable = useFeatureBit(
    FeatureBit.PRICE_TEST_REPORTS_NO_START_DATE,
  );
  if (isAdminAvailable) {
    return experience.isPriceTest;
  }
  const isEnabled =
    useFeatureBit(FeatureBit.PRICE_TEST_REPORTS) && experience.isPriceTest;
  const start = getMetricsStartDate();
  if (experience.id === `84628b1a-f001-4501-b03b-2bc6624403f5`) {
    return isEnabled;
  }
  const availableSince = new Date(`2025-08-10T00:00:00`).valueOf();
  return (
    isEnabled &&
    experience.metricsStartAt &&
    new Date(experience.metricsStartAt).valueOf() > availableSince &&
    start?.valueOf() > availableSince
  );
}

export function transformMetrics(
  metrics: SummaryResponse,
  alias: string,
): SummaryResponse {
  const storeEnrichMetrics = getStoreCustomEnrich(alias);
  if (isObjEmpty(storeEnrichMetrics)) return metrics;
  if (!metrics?.variants) return metrics;

  metrics.variants.forEach((variant) => {
    const baseMetrics = variant.metrics;
    if (!baseMetrics) return;
    Object.entries(storeEnrichMetrics).forEach(([key, def]) => {
      if (!def?.compute) return;
      const alreadyExists = baseMetrics.some((m) => m.name === key);
      if (!alreadyExists) {
        const computedValue = def.compute(baseMetrics);
        baseMetrics.push({
          name: key as MetricKind,
          value: computedValue,
        } as MetricValue);
      }
    });
  });
  return metrics;
}

async function summaryDataFetch(
  startDate: Date,
  endDate: Date,
  getSummary: (
    experienceId: string,
    goal: MetricKind,
    startAt: Date,
    endAt: Date,
    version?: number,
    expEndDate?: string | undefined,
    sessionType?: string,
    audience?: string,
    activeVariants?: string[],
    sections?: string[],
    outlierSmooth?: boolean,
    isPostPurchase?: boolean,
    isOtwForAll?: boolean,
    customMetrics?: { name: string; ordinal: number }[],
  ) => Promise<SummaryResponse>,
  experience: Experience,
  alias: string,
  v: number,
  version: ExpVersion,
  setData: (
    value: ((prevState: SummaryResponse) => SummaryResponse) | SummaryResponse,
  ) => void,
  sessionType?: string,
  audience?: string,
  activeVariants?: string[],
  outlierSmooth?: boolean,
  isOtwForAll?: boolean,
) {
  if (startDate && endDate) {
    const versionId = experience.isHidden ? -1 : v || version?.version;
    const customMetrics = experience?.elementTracking?.map((x) => ({
      name: x.name,
      ordinal: x.ordinal,
    }));
    let metrics = await getSummary(
      experience.id,
      experience.primaryGoal || experience.goals?.[0],
      startDate,
      endDate,
      versionId,
      experience?.archivedAt || experience?.endDate,
      sessionType,
      audience,
      activeVariants,
      [`stats`],
      outlierSmooth,
      experience.isPostPurchase,
      isOtwForAll,
      customMetrics,
    );
    metrics = transformMetrics(metrics, alias);
    setData(metrics);
    metrics = await getSummary(
      experience.id,
      experience.primaryGoal || experience.goals?.[0],
      startDate,
      endDate,
      versionId,
      experience?.archivedAt || experience?.endDate,
      sessionType,
      audience,
      activeVariants,
      [],
      outlierSmooth,
      experience.isPostPurchase,
      isOtwForAll,
      customMetrics,
    );
    metrics = transformMetrics(metrics, alias);
    setData(metrics);
  }
}

function calcFreeSlotsInFirstRow({
  hasOneVariant,
}: {
  hasOneVariant: boolean;
}) {
  let freeSlotsInFirstRow = 0; // 6 recs metrics + variant name + odds to win, 0 free slots
  if (hasOneVariant) {
    freeSlotsInFirstRow = 2; // 6 recs/custom metrics ,2 free slots
  }
  return freeSlotsInFirstRow;
}
